import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../components/shared/Navbar';
import SEO from '../components/SEO';
import API from '../utils/api';
import usePricing from '../utils/usePricing';
import { trackGiftPurchase } from '../utils/analytics';
import { openCheckoutUrl } from '../utils/twa';
import { shouldUseIap, purchaseProduct, getIapPriceMap, paymentStoreName } from '../utils/iap';
import { useAlert } from '../components/ConfirmModal';

// Mirrors backend/middleware/validate.js. `includes('@')` accepted "a@", so a
// gift could be filled in across five steps and only rejected at the payment
// step by the server — after the buyer had entered everything.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const LANGUAGES = [
  { code: 'en', label: 'English',    flag: '🇬🇧' },
  { code: 'es', label: 'Spanish',    flag: '🇪🇸' },
  { code: 'fr', label: 'French',     flag: '🇫🇷' },
  { code: 'hi', label: 'Hindi',      flag: '🇮🇳' },
  { code: 'gu', label: 'Gujarati',   flag: '🇮🇳' },
  { code: 'de', label: 'German',     flag: '🇩🇪' },
  { code: 'pt', label: 'Portuguese', flag: '🇧🇷' },
  { code: 'ar', label: 'Arabic',     flag: '🇸🇦' },
  { code: 'zh', label: 'Chinese',    flag: '🇨🇳' },
  { code: 'ja', label: 'Japanese',   flag: '🇯🇵' },
  { code: 'it', label: 'Italian',    flag: '🇮🇹' },
];

const INTERESTS = ['Dinosaurs','Space','Animals','Magic','Superheroes','Princesses','Cars','Nature','Music','Ocean','Dragons','Robots'];
const STORY_STEPS = ['Your details', "Child's details", 'Story style', 'Personal message', 'Review & pay'];

const TONES = [
  { value: 'magical',     icon: '✨', desc: 'Wands, spells & wonder' },
  { value: 'adventurous', icon: '🗺️', desc: 'Epic quests & discovery' },
  { value: 'funny',       icon: '😄', desc: 'Giggles & silly moments' },
  { value: 'calm',        icon: '🌙', desc: 'Peaceful & soothing' },
  { value: 'educational', icon: '📚', desc: 'Fun facts woven in' },
  { value: 'cozy',        icon: '🔥', desc: 'Warm & comforting' },
  { value: 'exciting',    icon: '⚡', desc: 'Fast-paced & thrilling' },
  { value: 'spooky',      icon: '👻', desc: 'Mildly spooky (age-safe)' },
];

const THEME_CATEGORIES = [
  { label: 'Life Lessons', icon: '💡', themes: ['Kindness','Bravery','Friendship','Sharing','Honesty','Patience','Curiosity','Gratitude','Perseverance','Empathy','Courage','Generosity'] },
  { label: 'Seasonal',     icon: '🎃', themes: ['🎃 Halloween','🎄 Christmas','🌸 Spring','☀️ Summer','🍂 Autumn','❄️ Winter','🐣 Easter','🎆 New Year'] },
  { label: 'Emotional',    icon: '💛', themes: ['New Sibling','Starting School','Fear of the Dark','Moving Home','Missing Someone','Making New Friends','Feeling Nervous','Losing a Pet','Dealing with Anger','Being Different'] },
];

const LENGTH_OPTIONS = [
  { value: 'short',  label: 'Short',  icon: '⚡', desc: '~5 min read' },
  { value: 'medium', label: 'Medium', icon: '📖', desc: '~8 min read' },
  { value: 'long',   label: 'Long',   icon: '📚', desc: '~12 min read' },
];

const LEARNING_CATEGORIES = [
  { label: 'Science & Nature',    icon: '🔬', topics: ['The Solar System','How Plants Grow','The Water Cycle','Volcanoes','Dinosaurs','Animals & Habitats','Weather & Seasons','The Human Body','Insects & Bugs','Ocean Life'] },
  { label: 'Maths',               icon: '🔢', topics: ['Counting & Numbers','Addition & Subtraction','Shapes & Geometry','Fractions','Telling Time','Measuring & Sizes','Patterns','Money & Coins'] },
  { label: 'Life Skills',         icon: '🌱', topics: ['Sharing & Taking Turns','Saying Sorry','Tidying Up','Washing Hands','Healthy Eating','Bedtime Routines','Road Safety','Stranger Danger','Managing Feelings'] },
  { label: 'History & Culture',   icon: '🏛️', topics: ['Ancient Egypt','Knights & Castles','Space Exploration','Famous Inventors','World Cultures','How Rainbows Form','Recycling & the Planet','Where Food Comes From'] },
  { label: 'Language & Literacy', icon: '📖', topics: ['Rhyming Words','The Alphabet','Opposites','Colours & Shades','Days of the Week','Months of the Year','Storytelling Structure'] },
];

// ── Story Gift ─────────────────────────────────────────────
const StoryGiftTab = ({ pricing, cancelled }) => {
  const alert = useAlert();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [iapGiftPrice, setIapGiftPrice] = useState(null); // localized store price on native

  useEffect(() => {
    if (shouldUseIap()) getIapPriceMap(['gift_story']).then(m => setIapGiftPrice(m.gift_story || null)).catch(() => {});
  }, []);
  const giftPrice = iapGiftPrice || pricing.formatted?.gift;
  const [activeThemeTab, setActiveThemeTab] = useState(0);
  const [activeLearningTab, setActiveLearningTab] = useState(0);

  const [form, setForm] = useState({
    senderName: '', senderEmail: '',
    recipientEmail: '', recipientName: '',
    childName: '', childAge: '',
    childGender: 'neutral',
    childInterests: [],
    language: 'en',
    themes: [],
    tone: '',
    storyLength: 'medium',
    learningTopic: '',
    customPrompt: '',
    personalMessage: '',
  });

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const toggleInterest = (i) =>
    setForm(f => ({
      ...f,
      childInterests: f.childInterests.includes(i)
        ? f.childInterests.filter(x => x !== i)
        : [...f.childInterests, i],
    }));

  const toggleTheme = (t) =>
    setForm(f => ({
      ...f,
      themes: f.themes.includes(t)
        ? f.themes.filter(x => x !== t)
        : f.themes.length < 5 ? [...f.themes, t] : f.themes,
    }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.senderName.trim()) return 'Your name is required.';
      if (!EMAIL_RE.test(form.senderEmail.trim())) return 'A valid email is required.';
      if (!EMAIL_RE.test(form.recipientEmail.trim())) return 'A valid recipient email is required.';
    }
    if (step === 1) {
      if (!form.childName.trim()) return "Child's name is required.";
      const age = parseInt(form.childAge, 10);
      if (!age || age < 1 || age > 12) return "Child's age must be between 1 and 12.";
    }
    return '';
  };

  const next = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError('');
    setStep(s => s + 1);
  };

  const handlePay = async () => {
    // iOS: create the pending gift, buy the consumable via Apple IAP; the
    // RevenueCat webhook then generates + emails the story.
    if (shouldUseIap()) {
      setError(''); setLoading(true);
      trackGiftPurchase('story');
      try {
        const res = await API.post('/gift/checkout', {
          ...form, childAge: parseInt(form.childAge, 10), currency: pricing.currency, iap: true,
        });
        if (!res.data?.iap || !res.data?.productId) throw new Error('Could not start the purchase.');
        const r = await purchaseProduct(res.data.productId);
        if (r.cancelled) { setLoading(false); return; }
        await alert({ title: 'Gift on its way! 🎁', message: `We’ll email the story to ${form.recipientEmail} shortly.` });
        setStep(0);
      } catch (err) {
        setError(err.message || err.response?.data?.message || 'Purchase failed. Please try again.');
      } finally { setLoading(false); }
      return;
    }
    setError(''); setLoading(true);
    trackGiftPurchase('story');
    try {
      const res = await API.post('/gift/checkout', {
        ...form,
        childAge: parseInt(form.childAge, 10),
        currency: pricing.currency,
      });
      openCheckoutUrl(res.data.url);
    } catch (err) {
      setError(err.response?.data?.message || 'Payment setup failed. Please try again.');
      setLoading(false);
    }
  };

  const selectedLang = LANGUAGES.find(l => l.code === form.language);

  return (
    <>
      {cancelled && (
        <div className="alert alert-warning mb-4 rounded-3">
          <i className="bi bi-info-circle me-2"></i>
          Payment was cancelled. Your details are still saved below — just click <strong>Pay</strong> when you're ready.
        </div>
      )}

      {/* Step indicator */}
      <div className="d-flex align-items-center justify-content-center gap-0 mb-5">
        {STORY_STEPS.map((label, i) => (
          <React.Fragment key={i}>
            <div className="d-flex flex-column align-items-center" style={{ minWidth: '60px' }}>
              <div className="d-flex align-items-center justify-content-center fw-bold"
                style={{
                  width: '36px', height: '36px', borderRadius: '50%', fontSize: '14px',
                  background: i <= step ? '#6f42c1' : '#e0d4f7',
                  color: i <= step ? 'white' : '#9b7dd4',
                  transition: 'all 0.2s',
                }}>
                {i < step ? <i className="bi bi-check2"></i> : i + 1}
              </div>
              <span className="mt-1 text-center"
                style={{ fontSize: '11px', color: i <= step ? '#6f42c1' : '#aaa', fontWeight: i === step ? 700 : 400, lineHeight: 1.2 }}>
                {label}
              </span>
            </div>
            {i < STORY_STEPS.length - 1 && (
              <div style={{ flex: 1, height: '2px', background: i < step ? '#6f42c1' : '#e0d4f7', margin: '0 4px', marginBottom: '22px', transition: 'all 0.2s' }} />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
        <div className="card-body p-4 p-lg-5">
          {error && <div className="alert alert-danger py-2 mb-4 small">{error}</div>}

          {step === 0 && (
            <div>
              <h5 className="fw-bold mb-4" style={{ color: 'var(--text-main)' }}>
                <i className="bi bi-person-fill me-2" style={{ color: '#6f42c1' }}></i>Your details
              </h5>
              <div className="row g-3 mb-4">
                <div className="col-sm-6">
                  <label className="form-label fw-semibold small">Your name <span className="text-danger">*</span></label>
                  <input className="form-control" placeholder="e.g. Grandma Sarah"
                    value={form.senderName} onChange={e => set('senderName', e.target.value)} />
                </div>
                <div className="col-sm-6">
                  <label className="form-label fw-semibold small">Your email <span className="text-danger">*</span></label>
                  <input className="form-control" type="email" placeholder="you@example.com"
                    value={form.senderEmail} onChange={e => set('senderEmail', e.target.value)} />
                  <div className="form-text">We'll send you a confirmation here.</div>
                </div>
              </div>
              <hr className="my-4" />
              <h5 className="fw-bold mb-4" style={{ color: 'var(--text-main)' }}>
                <i className="bi bi-envelope-fill me-2" style={{ color: '#6f42c1' }}></i>Deliver to
              </h5>
              <div className="row g-3">
                <div className="col-sm-6">
                  <label className="form-label fw-semibold small">Recipient email <span className="text-danger">*</span></label>
                  <input className="form-control" type="email" placeholder="parent@example.com"
                    value={form.recipientEmail} onChange={e => set('recipientEmail', e.target.value)} />
                  <div className="form-text">The story is emailed here.</div>
                </div>
                <div className="col-sm-6">
                  <label className="form-label fw-semibold small">Recipient name <span className="text-muted fw-normal">(optional)</span></label>
                  <input className="form-control" placeholder="e.g. Emma's Mum"
                    value={form.recipientName} onChange={e => set('recipientName', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h5 className="fw-bold mb-4" style={{ color: 'var(--text-main)' }}>
                <i className="bi bi-stars me-2" style={{ color: '#6f42c1' }}></i>About the child
              </h5>
              <div className="row g-3 mb-4">
                <div className="col-sm-6">
                  <label className="form-label fw-semibold small">Child's name <span className="text-danger">*</span></label>
                  <input className="form-control" placeholder="e.g. Emma"
                    value={form.childName} onChange={e => set('childName', e.target.value)} />
                  <div className="form-text">They'll star in their own story!</div>
                </div>
                <div className="col-sm-6">
                  <label className="form-label fw-semibold small">Child's age <span className="text-danger">*</span></label>
                  <select className="form-select" value={form.childAge} onChange={e => set('childAge', e.target.value)}>
                    <option value="">Select age…</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(a => (
                      <option key={a} value={a}>{a} year{a > 1 ? 's' : ''} old</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mb-4">
                <label className="form-label fw-semibold small">Gender</label>
                <div className="d-flex gap-2">
                  {[
                    { value: 'boy',     label: 'Boy',     icon: '👦' },
                    { value: 'girl',    label: 'Girl',    icon: '👧' },
                    { value: 'neutral', label: 'Neutral', icon: '🧒' },
                  ].map(g => (
                    <button key={g.value} type="button"
                      className={`btn btn-sm rounded-pill px-4 ${form.childGender === g.value ? 'btn-dream' : 'btn-outline-secondary'}`}
                      onClick={() => set('childGender', g.value)}>
                      {g.icon} {g.label}
                    </button>
                  ))}
                </div>
                <div className="form-text">Helps the story use the right pronouns.</div>
              </div>
              <div className="mb-4">
                <label className="form-label fw-semibold small">
                  Interests <span className="text-muted fw-normal">(optional — helps personalise the story)</span>
                </label>
                <div className="d-flex flex-wrap gap-2">
                  {INTERESTS.map(i => (
                    <button key={i} type="button"
                      className={`btn btn-sm rounded-pill ${form.childInterests.includes(i) ? 'btn-dream' : 'btn-outline-secondary'}`}
                      onClick={() => toggleInterest(i)}>
                      {i}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="form-label fw-semibold small">Story language</label>
                <div className="d-flex flex-wrap gap-2">
                  {LANGUAGES.map(lang => (
                    <button key={lang.code} type="button"
                      className={`btn btn-sm rounded-pill ${form.language === lang.code ? 'btn-dream' : 'btn-outline-secondary'}`}
                      onClick={() => set('language', lang.code)}>
                      {lang.flag} {lang.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h5 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>
                <i className="bi bi-magic me-2" style={{ color: '#6f42c1' }}></i>Story style
              </h5>
              <p className="text-muted small mb-4">All optional — skip ahead if you're happy with a surprise!</p>

              {/* Themes */}
              <div className="mb-4">
                <label className="form-label fw-semibold small">Themes <span className="text-muted fw-normal">(pick up to 5)</span></label>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {THEME_CATEGORIES.map((cat, i) => (
                    <button key={i} type="button"
                      className={`btn btn-sm rounded-pill ${activeThemeTab === i ? 'btn-dream' : 'btn-outline-secondary'}`}
                      onClick={() => setActiveThemeTab(i)}>
                      {cat.icon} {cat.label}
                    </button>
                  ))}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {THEME_CATEGORIES[activeThemeTab].themes.map(t => (
                    <button key={t} type="button"
                      className={`btn btn-sm rounded-pill ${form.themes.includes(t) ? 'btn-dream' : 'btn-outline-secondary'}`}
                      onClick={() => toggleTheme(t)}
                      disabled={!form.themes.includes(t) && form.themes.length >= 5}>
                      {t}
                    </button>
                  ))}
                </div>
                {form.themes.length > 0 && (
                  <div className="mt-2 small text-muted">Selected: {form.themes.join(' · ')}</div>
                )}
              </div>

              {/* Tone */}
              <div className="mb-4">
                <label className="form-label fw-semibold small">Story tone</label>
                <div className="row g-2">
                  {TONES.map(t => (
                    <div className="col-6 col-sm-3" key={t.value}>
                      <div onClick={() => set('tone', form.tone === t.value ? '' : t.value)}
                        style={{
                          border: `2px solid ${form.tone === t.value ? '#6f42c1' : '#e0d4f7'}`,
                          borderRadius: '12px', padding: '10px 8px', cursor: 'pointer', textAlign: 'center',
                          background: form.tone === t.value ? '#f8f5ff' : '#fff', transition: 'all 0.15s',
                        }}>
                        <div style={{ fontSize: '20px', marginBottom: '2px' }}>{t.icon}</div>
                        <div className="fw-semibold" style={{ fontSize: '12px', color: 'var(--text-main)', textTransform: 'capitalize' }}>{t.value}</div>
                        <div className="text-muted" style={{ fontSize: '10px' }}>{t.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Story length */}
              <div className="mb-4">
                <label className="form-label fw-semibold small">Story length</label>
                <div className="row g-2">
                  {LENGTH_OPTIONS.map(o => (
                    <div className="col-4" key={o.value}>
                      <div onClick={() => set('storyLength', o.value)}
                        style={{
                          border: `2px solid ${form.storyLength === o.value ? '#6f42c1' : '#e0d4f7'}`,
                          borderRadius: '12px', padding: '12px 8px', cursor: 'pointer', textAlign: 'center',
                          background: form.storyLength === o.value ? '#f8f5ff' : '#fff', transition: 'all 0.15s',
                        }}>
                        <div style={{ fontSize: '20px', marginBottom: '2px' }}>{o.icon}</div>
                        <div className="fw-semibold" style={{ fontSize: '13px', color: 'var(--text-main)' }}>{o.label}</div>
                        <div className="text-muted" style={{ fontSize: '11px' }}>{o.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Learning topic */}
              <div className="mb-4">
                <label className="form-label fw-semibold small">Learning topic <span className="text-muted fw-normal">(optional)</span></label>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {LEARNING_CATEGORIES.map((cat, i) => (
                    <button key={i} type="button"
                      className={`btn btn-sm rounded-pill ${activeLearningTab === i ? 'btn-dream' : 'btn-outline-secondary'}`}
                      onClick={() => setActiveLearningTab(i)}>
                      {cat.icon} {cat.label}
                    </button>
                  ))}
                </div>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {LEARNING_CATEGORIES[activeLearningTab].topics.map(topic => (
                    <button key={topic} type="button"
                      className={`btn btn-sm rounded-pill ${form.learningTopic === topic ? 'btn-dream' : 'btn-outline-secondary'}`}
                      onClick={() => set('learningTopic', form.learningTopic === topic ? '' : topic)}>
                      {topic}
                    </button>
                  ))}
                </div>
                <input className="form-control form-control-sm mt-1" placeholder="Or type your own topic…"
                  maxLength={200}
                  value={form.learningTopic}
                  onChange={e => set('learningTopic', e.target.value)} />
              </div>

              {/* Special request */}
              <div>
                <label className="form-label fw-semibold small">Special request <span className="text-muted fw-normal">(optional)</span></label>
                <textarea className="form-control" rows={3}
                  placeholder={`e.g. Make ${form.childName || 'the child'} find a dragon egg at the end`}
                  maxLength={500}
                  value={form.customPrompt}
                  onChange={e => set('customPrompt', e.target.value)} />
                <div className="form-text text-end">{form.customPrompt.length}/500</div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>
                <i className="bi bi-chat-heart-fill me-2" style={{ color: '#6f42c1' }}></i>Add a personal message
              </h5>
              <p className="text-muted small mb-4">This will appear inside the email before the story begins.</p>
              <textarea className="form-control mb-2" rows={4}
                placeholder={`e.g. Happy birthday ${form.childName || 'sweetheart'}! I hope this story makes your night magical. Love you lots! 🌙`}
                maxLength={400}
                value={form.personalMessage} onChange={e => set('personalMessage', e.target.value)} />
              <div className="text-muted small text-end">{form.personalMessage.length}/400</div>
              {form.personalMessage.trim() && (
                <div className="mt-4 p-4 rounded-3" style={{ background: 'var(--page-bg)', border: '2px dashed #c9a8ff' }}>
                  <p className="text-muted small fw-semibold mb-2">Preview:</p>
                  <p className="fst-italic mb-1" style={{ color: '#6f42c1' }}>"{form.personalMessage}"</p>
                  <p className="mb-0 small" style={{ color: '#a090c0' }}>— {form.senderName || 'You'}</p>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div>
              <h5 className="fw-bold mb-4" style={{ color: 'var(--text-main)' }}>
                <i className="bi bi-check-circle-fill me-2" style={{ color: '#6f42c1' }}></i>Review your gift
              </h5>
              <div className="mb-3 p-3 rounded-3" style={{ background: 'var(--page-bg)' }}>
                <div className="row g-2 small">
                  <div className="col-5 text-muted">From</div>
                  <div className="col-7 fw-semibold" style={{ color: 'var(--text-main)' }}>{form.senderName} ({form.senderEmail})</div>
                  <div className="col-5 text-muted">Delivering to</div>
                  <div className="col-7 fw-semibold" style={{ color: 'var(--text-main)' }}>{form.recipientEmail}</div>
                  <div className="col-5 text-muted">Story for</div>
                  <div className="col-7 fw-semibold" style={{ color: 'var(--text-main)' }}>
                    {form.childName}, age {form.childAge}
                    {form.childGender !== 'neutral' && <span className="text-muted fw-normal"> · {form.childGender}</span>}
                  </div>
                  {form.childInterests.length > 0 && (
                    <>
                      <div className="col-5 text-muted">Interests</div>
                      <div className="col-7" style={{ color: 'var(--text-main)' }}>{form.childInterests.join(', ')}</div>
                    </>
                  )}
                  <div className="col-5 text-muted">Language</div>
                  <div className="col-7" style={{ color: 'var(--text-main)' }}>{selectedLang?.flag} {selectedLang?.label}</div>
                  {form.themes.length > 0 && (
                    <>
                      <div className="col-5 text-muted">Themes</div>
                      <div className="col-7" style={{ color: 'var(--text-main)' }}>{form.themes.join(', ')}</div>
                    </>
                  )}
                  {form.tone && (
                    <>
                      <div className="col-5 text-muted">Tone</div>
                      <div className="col-7" style={{ color: 'var(--text-main)', textTransform: 'capitalize' }}>{form.tone}</div>
                    </>
                  )}
                  <div className="col-5 text-muted">Length</div>
                  <div className="col-7" style={{ color: 'var(--text-main)', textTransform: 'capitalize' }}>{form.storyLength}</div>
                  {form.learningTopic && (
                    <>
                      <div className="col-5 text-muted">Learning topic</div>
                      <div className="col-7" style={{ color: 'var(--text-main)' }}>{form.learningTopic}</div>
                    </>
                  )}
                  {form.personalMessage && (
                    <>
                      <div className="col-5 text-muted">Message</div>
                      <div className="col-7 fst-italic" style={{ color: '#6f42c1' }}>
                        "{form.personalMessage.substring(0, 80)}{form.personalMessage.length > 80 ? '…' : ''}"
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-between p-3 rounded-3 mb-4"
                style={{ background: 'linear-gradient(135deg,#f0e8ff,#e8d5ff)', border: '2px solid #c9a8ff' }}>
                <div>
                  <div className="fw-bold" style={{ color: 'var(--text-main)' }}>One personalised bedtime story</div>
                  <div className="text-muted small">Personalised · Delivered by email · Instantly</div>
                </div>
                <div className="fw-bold fs-4" style={{ color: '#6f42c1' }}>{giftPrice}</div>
              </div>
              <button className="btn btn-dream btn-lg w-100 rounded-pill fw-bold"
                onClick={handlePay} disabled={loading}>
                {loading
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>{shouldUseIap() ? 'Processing…' : 'Redirecting to payment…'}</>
                  : <><i className="bi bi-lock-fill me-2"></i>Pay {giftPrice} &amp; Send the Gift</>}
              </button>
              <p className="text-muted small text-center mt-2">
                <i className="bi bi-shield-lock me-1"></i>Secure payment {paymentStoreName() ? `through ${paymentStoreName()}` : 'via Stripe'}. Story delivered within minutes.
              </p>
            </div>
          )}

          <div className={`d-flex mt-4 ${step > 0 ? 'justify-content-between' : 'justify-content-end'}`}>
            {step > 0 && (
              <button className="btn btn-outline-secondary rounded-pill px-4"
                onClick={() => { setError(''); setStep(s => s - 1); }}>
                <i className="bi bi-arrow-left me-1"></i>Back
              </button>
            )}
            {step < STORY_STEPS.length - 1 && (
              <button className="btn btn-dream rounded-pill px-5 fw-semibold" onClick={next}>
                Continue <i className="bi bi-arrow-right ms-1"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ── Subscription Gift ──────────────────────────────────────
const DURATION_OPTIONS = [
  { months: 1, label: '1 Month',  badge: null },
  { months: 3, label: '3 Months', badge: 'Popular' },
  { months: 6, label: '6 Months', badge: 'Best value' },
];

const SubscriptionGiftTab = ({ pricing }) => {
  const alert = useAlert();
  const [plan, setPlan]           = useState('basic');
  const [duration, setDuration]   = useState(3);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [iapSubPrices, setIapSubPrices] = useState({}); // productId → localized store price (native)

  useEffect(() => {
    if (!shouldUseIap()) return;
    const ids = [];
    for (const p of ['basic', 'family']) for (const m of [1, 3, 6]) ids.push(`gift_${p}_${m}mo`);
    // The plan picker's "/mo" reference figure — store price on the apps, so it
    // matches the rest of the store-priced flow rather than our backend's.
    ids.push('basic_monthly', 'family_monthly');
    getIapPriceMap(ids).then(setIapSubPrices).catch(() => {});
  }, []);

  const [form, setForm] = useState({
    senderName: '', senderEmail: '',
    recipientEmail: '', recipientName: '',
    personalMessage: '',
  });

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // Price calculation
  const monthlyPrice = plan === 'basic' ? pricing.basicUnit : pricing.familyUnit;
  const totalUnit    = monthlyPrice * duration;
  const sym          = pricing.symbol || '$';
  const totalFormatted = pricing.currencyCode === 'jpy' || pricing.currencyCode === 'krw'
    ? `${sym}${totalUnit}`
    : `${sym}${(totalUnit / 100).toFixed(2).replace('.00', '')}`;
  // On native, show the real store price for this plan+duration gift product.
  const displayTotal = iapSubPrices[`gift_${plan}_${duration}mo`] || totalFormatted;

  const handlePay = async () => {
    if (!form.senderName.trim()) return setError('Your name is required.');
    if (!EMAIL_RE.test(form.senderEmail.trim())) return setError('A valid email is required.');
    if (!EMAIL_RE.test(form.recipientEmail.trim())) return setError('A valid recipient email is required.');

    // iOS: create the pending gift, buy via Apple IAP; the RevenueCat webhook
    // then emails the recipient their claim link.
    if (shouldUseIap()) {
      setError(''); setLoading(true);
      trackGiftPurchase('subscription');
      try {
        const res = await API.post('/gift/subscription/checkout', {
          plan, durationMonths: duration, ...form, currency: pricing.currency, iap: true,
        });
        if (!res.data?.iap || !res.data?.productId) throw new Error('Could not start the purchase.');
        const r = await purchaseProduct(res.data.productId);
        if (r.cancelled) { setLoading(false); return; }
        await alert({ title: 'Gift on its way! 🎁', message: `We’ll email the claim link to ${form.recipientEmail} shortly.` });
      } catch (err) {
        setError(err.message || err.response?.data?.message || 'Purchase failed. Please try again.');
      } finally { setLoading(false); }
      return;
    }
    setError(''); setLoading(true);
    trackGiftPurchase('subscription');
    try {
      const res = await API.post('/gift/subscription/checkout', {
        plan,
        durationMonths: duration,
        ...form,
        currency: pricing.currency,
      });
      openCheckoutUrl(res.data.url);
    } catch (err) {
      setError(err.response?.data?.message || 'Payment setup failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
      <div className="card-body p-4 p-lg-5">
        {error && <div className="alert alert-danger py-2 mb-4 small">{error}</div>}

        {/* Plan picker */}
        <h5 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>
          <i className="bi bi-stars me-2" style={{ color: '#6f42c1' }}></i>Choose a plan
        </h5>
        <div className="row g-3 mb-4">
          {[
            {
              key: 'basic',
              label: 'Basic',
              price: iapSubPrices.basic_monthly || pricing.formatted?.basic || `${sym}9`,
              perks: ['Unlimited stories', 'Multiple children', 'All languages'],
            },
            {
              key: 'family',
              label: 'Family',
              price: iapSubPrices.family_monthly || pricing.formatted?.family || `${sym}15`,
              perks: ['Everything in Basic', 'Voice cloning', 'Family sharing', 'Priority generation'],
            },
          ].map(p => (
            <div className="col-sm-6" key={p.key}>
              <div
                onClick={() => setPlan(p.key)}
                style={{
                  border: `2px solid ${plan === p.key ? '#6f42c1' : '#e0d4f7'}`,
                  borderRadius: '16px',
                  padding: '16px',
                  cursor: 'pointer',
                  background: plan === p.key ? '#f8f5ff' : '#fff',
                  transition: 'all 0.15s',
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-bold" style={{ color: 'var(--text-main)' }}>{p.label}</span>
                  <span className="fw-bold" style={{ color: '#6f42c1' }}>{p.price}<span className="text-muted fw-normal" style={{ fontSize: '12px' }}>/mo</span></span>
                </div>
                <ul className="list-unstyled mb-0" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>
                  {p.perks.map(perk => (
                    <li key={perk}><i className="bi bi-check2 me-1" style={{ color: '#6f42c1' }}></i>{perk}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Duration picker */}
        <h5 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>
          <i className="bi bi-calendar3 me-2" style={{ color: '#6f42c1' }}></i>How long?
        </h5>
        <div className="row g-3 mb-4">
          {DURATION_OPTIONS.map(opt => (
            <div className="col-4" key={opt.months}>
              <div
                onClick={() => setDuration(opt.months)}
                style={{
                  border: `2px solid ${duration === opt.months ? '#6f42c1' : '#e0d4f7'}`,
                  borderRadius: '14px',
                  padding: '14px 8px',
                  cursor: 'pointer',
                  background: duration === opt.months ? '#f8f5ff' : '#fff',
                  textAlign: 'center',
                  transition: 'all 0.15s',
                  position: 'relative',
                }}
              >
                {opt.badge && (
                  <span style={{
                    position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)',
                    background: '#6f42c1', color: '#fff', fontSize: '10px', fontWeight: 700,
                    padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap',
                  }}>{opt.badge}</span>
                )}
                <div className="fw-bold" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{opt.label}</div>
                <div style={{ color: '#6f42c1', fontSize: '13px', fontWeight: 600 }}>
                  {sym}{((monthlyPrice * opt.months) / 100).toFixed(0)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Sender details */}
        <h5 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>
          <i className="bi bi-person-fill me-2" style={{ color: '#6f42c1' }}></i>Your details
        </h5>
        <div className="row g-3 mb-4">
          <div className="col-sm-6">
            <label className="form-label fw-semibold small">Your name <span className="text-danger">*</span></label>
            <input className="form-control" placeholder="e.g. Grandma Sarah"
              value={form.senderName} onChange={e => set('senderName', e.target.value)} />
          </div>
          <div className="col-sm-6">
            <label className="form-label fw-semibold small">Your email <span className="text-danger">*</span></label>
            <input className="form-control" type="email" placeholder="you@example.com"
              value={form.senderEmail} onChange={e => set('senderEmail', e.target.value)} />
            <div className="form-text">Confirmation sent here.</div>
          </div>
        </div>

        {/* Recipient details */}
        <h5 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>
          <i className="bi bi-envelope-fill me-2" style={{ color: '#6f42c1' }}></i>Deliver to
        </h5>
        <div className="row g-3 mb-4">
          <div className="col-sm-6">
            <label className="form-label fw-semibold small">Recipient email <span className="text-danger">*</span></label>
            <input className="form-control" type="email" placeholder="parent@example.com"
              value={form.recipientEmail} onChange={e => set('recipientEmail', e.target.value)} />
            <div className="form-text">Claim link sent here.</div>
          </div>
          <div className="col-sm-6">
            <label className="form-label fw-semibold small">Recipient name <span className="text-muted fw-normal">(optional)</span></label>
            <input className="form-control" placeholder="e.g. Emma's Mum"
              value={form.recipientName} onChange={e => set('recipientName', e.target.value)} />
          </div>
        </div>

        {/* Personal message */}
        <div className="mb-4">
          <label className="form-label fw-semibold small">
            <i className="bi bi-chat-heart me-1" style={{ color: '#6f42c1' }}></i>
            Personal message <span className="text-muted fw-normal">(optional)</span>
          </label>
          <textarea className="form-control" rows={3}
            placeholder="e.g. Wishing you endless magical bedtimes! Love you 🌙"
            maxLength={400}
            value={form.personalMessage} onChange={e => set('personalMessage', e.target.value)} />
          <div className="text-muted small text-end mt-1">{form.personalMessage.length}/400</div>
        </div>

        {/* Price summary */}
        <div className="d-flex align-items-center justify-content-between p-3 rounded-3 mb-4"
          style={{ background: 'linear-gradient(135deg,#f0e8ff,#e8d5ff)', border: '2px solid #c9a8ff' }}>
          <div>
            <div className="fw-bold" style={{ color: 'var(--text-main)' }}>
              {plan === 'basic' ? 'Basic' : 'Family'} — {DURATION_OPTIONS.find(o => o.months === duration)?.label}
            </div>
            <div className="text-muted small">Gift subscription · Delivered by email as a claim link</div>
          </div>
          <div className="fw-bold fs-4" style={{ color: '#6f42c1' }}>{displayTotal}</div>
        </div>

        <button className="btn btn-dream btn-lg w-100 rounded-pill fw-bold"
          onClick={handlePay} disabled={loading}>
          {loading
            ? <><span className="spinner-border spinner-border-sm me-2"></span>{shouldUseIap() ? 'Processing…' : 'Redirecting to payment…'}</>
            : <><i className="bi bi-lock-fill me-2"></i>Pay {displayTotal} &amp; Send the Gift</>}
        </button>
        <p className="text-muted small text-center mt-2">
          <i className="bi bi-shield-lock me-1"></i>Secure payment {paymentStoreName() ? `through ${paymentStoreName()}` : 'via Stripe'}. Recipient claims via email link — no credit card needed to redeem.
        </p>
      </div>
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────
const GiftStoryPage = () => {
  const { pricing } = usePricing();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled') === 'true';
  const [giftType, setGiftType] = useState(
    searchParams.get('type') === 'subscription' ? 'subscription' : 'story'
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      <SEO
        title="Gift a Bedtime Story Subscription"
        description="Give the gift of magical personalised bedtime stories. Choose a Dreamy Bedtime plan for someone you love — perfect for any occasion."
        canonical="/gift"
      />
      <Navbar />

      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)', padding: '56px 0 40px' }}>
        <div className="container text-center text-white" style={{ maxWidth: '680px' }}>
          <div style={{ fontSize: '52px', marginBottom: '12px' }}>🎁</div>
          <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(28px,5vw,40px)' }}>
            Give the Gift of Bedtime Magic
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: '17px', maxWidth: '520px', margin: '0 auto 24px' }}>
            A one-time story or a full subscription — either way, you're gifting nights full of wonder.
          </p>

          {/* Type tabs */}
          <div className="d-inline-flex rounded-pill p-1"
            style={{ background: 'rgba(255,255,255,0.12)', gap: '4px' }}>
            <button
              onClick={() => setGiftType('story')}
              className="btn rounded-pill fw-semibold px-4 py-2"
              style={{
                background: giftType === 'story' ? '#fff' : 'transparent',
                color: giftType === 'story' ? '#6f42c1' : '#fff',
                fontSize: '14px',
                border: 'none',
                transition: 'all 0.2s',
              }}>
              📖 Single Story
              <span className="ms-2 badge rounded-pill"
                style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '11px' }}>
                {pricing.formatted.gift}
              </span>
            </button>
            <button
              onClick={() => setGiftType('subscription')}
              className="btn rounded-pill fw-semibold px-4 py-2"
              style={{
                background: giftType === 'subscription' ? '#fff' : 'transparent',
                color: giftType === 'subscription' ? '#6f42c1' : '#fff',
                fontSize: '14px',
                border: 'none',
                transition: 'all 0.2s',
              }}>
              🌟 Subscription
              <span className="ms-2 badge rounded-pill"
                style={{ background: giftType === 'subscription' ? '#6f42c1' : 'rgba(255,255,255,0.2)', color: giftType === 'subscription' ? '#fff' : '#fff', fontSize: '11px' }}>
                1–6 months
              </span>
            </button>
          </div>
        </div>
      </section>

      <div className="container py-5" style={{ maxWidth: '640px' }}>
        {giftType === 'story'
          ? <StoryGiftTab pricing={pricing} cancelled={cancelled} />
          : <SubscriptionGiftTab pricing={pricing} />
        }

        {/* Trust signals */}
        <div className="row g-3 mt-4 text-center">
          {[
            ['🔒', 'Secure payment', paymentStoreName() ? `through ${paymentStoreName()}` : 'via Stripe'],
            ['⚡', 'Instant delivery', 'Email in minutes'],
            ['🎨', 'Fully personalised', giftType === 'story' ? 'Personalised story' : 'Premium subscription'],
          ].map(([icon, title, sub]) => (
            <div className="col-4" key={title}>
              <div className="fs-4">{icon}</div>
              <div className="fw-semibold small" style={{ color: 'var(--text-main)' }}>{title}</div>
              <div className="text-muted" style={{ fontSize: '11px' }}>{sub}</div>
            </div>
          ))}
        </div>

        <div className="text-center mt-4">
          <p className="text-muted small">
            Already have an account?{' '}
            <Link to="/dashboard/generate" style={{ color: '#6f42c1' }}>Generate unlimited stories</Link>
            {' · '}
            <Link to="/pricing" style={{ color: '#6f42c1' }}>See all plans</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default GiftStoryPage;
